/**
 * The design tokens: every colour, size and shadow the UI uses, for light and dark mode. The one
 * source for both the antd theme (tokens.ts) and the CSS variables the landing page and the
 * small UI components read (cssVars.ts). Free of antd, so the landing page can import it.
 *
 * Colour roles, not shades: `primary` is what a button is filled with, `link` is green text,
 * `focus` is the focus ring. Text/background pairs are checked against WCAG AA in
 * palette.test.ts — change a value there, and that test says whether it still passes.
 */

/**
 * WhatsApp-native identity: WhatsApp is the product's core, so the brand comes from it. The
 * bright green is for the logo, the hero and illustrations — as text or under white text it's
 * under 2:1, so buttons and links use `accent` instead.
 */
export const brand = {
  green: '#25D366',
  greenDark: '#1DA851',
  charcoal: '#111B21',
  panelDark: '#202C33',
  bgDark: '#0B141A',
  bgLight: '#F3F0E9',
} as const;

/** The brand green darkened until white text on it passes AA (5.41:1), with its hover and press. */
export const accent = {
  base: '#0E7A43',
  hover: '#0B6E3A',
  active: '#095C31',
} as const;

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface ModeColors {
  /** The page behind everything. */
  bg: string;
  /** Cards, tables, inputs, the sidebar. */
  surface: string;
  /** A step back from `surface`: table header, row hover, inset areas. */
  surfaceSubtle: string;
  border: string;
  borderSubtle: string;
  text: string;
  textMuted: string;
  /** Placeholders and the quietest labels — still AA on `surface`. */
  textSubtle: string;
  primary: string;
  primaryHover: string;
  primaryActive: string;
  /** Text on a `primary` fill. */
  onPrimary: string;
  link: string;
  linkHover: string;
  focus: string;
  /** For icons, borders and bars. */
  tone: Record<Tone, string>;
  /** Text in that tone, AA on `surface` and on its own `toneBg`. */
  toneText: Record<Tone, string>;
  /** A badge's tinted background. */
  toneBg: Record<Tone, string>;
  /** A filled danger button and the text on it. */
  dangerFill: string;
  onDangerFill: string;
  shadow: { sm: string; md: string; lg: string };
}

export const light: ModeColors = {
  bg: brand.bgLight,
  surface: '#FDFDFB',
  surfaceSubtle: '#F7F6F2',
  border: '#D3D6D2',
  borderSubtle: '#E6E4DE',
  text: brand.charcoal,
  textMuted: '#54656F',
  textSubtle: '#5F6F78',
  primary: accent.base,
  primaryHover: accent.hover,
  primaryActive: accent.active,
  onPrimary: '#FDFDFB',
  link: accent.base,
  linkHover: accent.hover,
  focus: accent.base,
  tone: {
    success: accent.base,
    warning: '#B26A00',
    danger: '#B42318',
    info: '#0A6C9E',
    neutral: '#54656F',
  },
  toneText: {
    success: accent.base,
    warning: '#8A5300',
    danger: '#B42318',
    info: '#0A6C9E',
    neutral: '#54656F',
  },
  toneBg: {
    success: '#E6F4EC',
    warning: '#FFF1D6',
    danger: '#FDECEA',
    info: '#E3F2FA',
    neutral: '#EDEFEE',
  },
  dangerFill: '#B42318',
  onDangerFill: '#FDFDFB',
  shadow: {
    sm: '0 1px 2px rgba(17, 27, 33, 0.06), 0 1px 1px rgba(17, 27, 33, 0.04)',
    md: '0 4px 12px rgba(17, 27, 33, 0.08), 0 1px 3px rgba(17, 27, 33, 0.06)',
    lg: '0 12px 32px rgba(17, 27, 33, 0.12), 0 2px 6px rgba(17, 27, 33, 0.08)',
  },
};

export const dark: ModeColors = {
  bg: brand.bgDark,
  surface: brand.panelDark,
  surfaceSubtle: '#1B2933',
  border: '#3B4A54',
  borderSubtle: '#2A3942',
  text: '#E9EDEF',
  textMuted: '#AEBAC1',
  textSubtle: '#9AA8B0',
  // Buttons keep the same fill as in light mode, so white text stays AA.
  primary: accent.base,
  primaryHover: accent.hover,
  primaryActive: accent.active,
  onPrimary: '#FDFDFB',
  // Green text and focus rings on a dark background need the bright green: the accent is 2.6:1.
  link: brand.green,
  linkHover: '#5BE38F',
  focus: brand.green,
  tone: {
    success: brand.green,
    warning: '#FFB020',
    danger: '#FF8A7E',
    info: '#53BDEB',
    neutral: '#AEBAC1',
  },
  toneText: {
    success: brand.green,
    warning: '#FFB020',
    danger: '#FF8A7E',
    info: '#53BDEB',
    neutral: '#AEBAC1',
  },
  toneBg: {
    success: 'rgba(37, 211, 102, 0.14)',
    warning: 'rgba(255, 176, 32, 0.14)',
    danger: 'rgba(242, 107, 94, 0.14)',
    info: 'rgba(83, 189, 235, 0.14)',
    neutral: 'rgba(233, 237, 239, 0.08)',
  },
  dangerFill: '#FF8A7E',
  onDangerFill: brand.bgDark,
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.32)',
    md: '0 4px 12px rgba(0, 0, 0, 0.36), 0 1px 3px rgba(0, 0, 0, 0.28)',
    lg: '0 12px 32px rgba(0, 0, 0, 0.48), 0 2px 6px rgba(0, 0, 0, 0.32)',
  },
};

/** Inter for Latin; Devanagari falls through to Noto Sans Devanagari, both loaded in index.html. */
export const fontFamily =
  "'Inter', 'Noto Sans Devanagari', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/** The type scale (px) and the line height that goes with each size. */
export const fontSize = { xs: 12, sm: 14, md: 16, lg: 20, xl: 24, xxl: 32 } as const;
export const lineHeight = { xs: 1.5, sm: 1.57, md: 1.5, lg: 1.4, xl: 1.33, xxl: 1.25 } as const;
export const fontWeight = { regular: 400, medium: 500, semibold: 600, bold: 700 } as const;

/** An 8px grid, with 4 for the tight spots. */
export const space = { 0.5: 4, 1: 8, 1.5: 12, 2: 16, 3: 24, 4: 32, 5: 40, 6: 48, 8: 64 } as const;

export const radius = { sm: 6, md: 8, lg: 12, pill: 999 } as const;

/** Hover and focus changes, and the fade-in for content. */
export const motion = {
  fast: '150ms',
  base: '200ms',
  easing: 'cubic-bezier(0.2, 0, 0, 1)',
} as const;
